import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import { loadGoogleTag } from './utils/gtag';
import { SeoProvider } from './context/SeoContext';
import SeoManager from './components/SeoManager';
import FloatingChatButton from './components/FloatingChatButton';
import CookieConsentBox from './components/CookieConsentBox';
import HoroscopePage from './components/Horoscope/HoroscopePage';
import { SHOP_PUBLIC_ENABLED } from './utils/shopPublic';

const Home = lazy(() => import('./components/Home'));
const ShopList = lazy(() => import('./components/Shop/ShopList'));
const Cart = lazy(() => import('./components/Shop/Cart'));
const Checkout = lazy(() => import('./components/Shop/Checkout'));
const OrderSuccess = lazy(() => import('./components/Shop/OrderSuccess'));
const SlugPage = lazy(() => import('./components/SlugPage'));
const BlogList = lazy(() => import('./components/BlogList'));
const NotFound = lazy(() => import('./components/NotFound'));
const HoroscopeArchivePage = lazy(() => import('./components/Horoscope/HoroscopeArchivePage'));
const FazaKsiezycaPage = lazy(() => import('./components/FazaKsiezycaPage'));
const ExpertListPage = lazy(() => import('./components/ExpertListPage'));
const ChatPage = lazy(() => import('./components/Chat/ChatPage'));
const PortalPage = lazy(() => import('./components/Portal/PortalPage'));
const LegalPage = lazy(() => import('./components/Static/LegalPage'));
const EnergyRitualPage = lazy(() => import('./components/EnergyRitualPage'));
const TarotPage = lazy(() => import('./components/Tarot/TarotPage'));
const InitiationPage = lazy(() => import('./components/InitiationPage'));

// Lazy-loaded CMS modules so they are only fetched when visiting /cms
const CMS = lazy(() => import('./components/CMS'));
const ProductEditor = lazy(() => import('./components/CMS/ProductEditor'));
const OrderDetail = lazy(() => import('./components/CMS/OrderDetail'));
const ArticleEditor = lazy(() => import('./components/CMS/ArticleEditor'));
const HoroscopeEditor = lazy(() => import('./components/CMS/HoroscopeEditor'));

/** Ciemne tło jak `bg-mystic-bg` — pod lazy route (bez FOUC na biel). */
const routeSuspenseFallback = (
  <div style={{ minHeight: '100vh', backgroundColor: '#0B0F14' }} aria-hidden />
);

const App: React.FC = () => {
  useEffect(() => {
    if (window.location.pathname.startsWith('/cms')) return;
    loadGoogleTag();
  }, []);

  return (
    <BrowserRouter>
        <SeoProvider>
          <SeoManager />
          <Suspense fallback={routeSuspenseFallback}>
            <Routes>
              <Route path="/" element={<Home />} />
              
              {SHOP_PUBLIC_ENABLED && (
                <>
                  <Route path="/sklep" element={<ShopList />} />
                  <Route path="/koszyk" element={<Cart />} />
                  <Route path="/zamowienie" element={<Checkout />} />
                  <Route path="/zamowienie-zlozone" element={<OrderSuccess />} />
                </>
              )}
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/strona/:pageNumber" element={<BlogList />} />
              <Route path="/eksperci" element={<ExpertListPage />} />
              <Route path="/eksperci/strona/:pageNumber" element={<ExpertListPage />} />
              <Route path="/czat" element={<ChatPage />} />
              <Route path="/sekcja-prawna" element={<LegalPage />} />
              <Route path="/portal/*" element={<PortalPage />} />
              <Route path="/przyjmij-energie" element={<EnergyRitualPage />} />
              <Route path="/tarot" element={<TarotPage />} />
              <Route path="/wtajemniczenie" element={<InitiationPage />} />

              <Route path="/horoskop" element={<HoroscopePage />} />
              <Route path="/archiwum-horoskopow" element={<HoroscopeArchivePage />} />
              <Route path="/faza-ksiezyca" element={<FazaKsiezycaPage />} />

              {/* Admin routes are now protected within the CMS component based on user rank */}
              <Route path="/cms/products/new" element={<ProductEditor />} />
              <Route path="/cms/products/edit/:id" element={<ProductEditor />} />
              <Route path="/cms/horoscope/new" element={<HoroscopeEditor />} />
              <Route path="/cms/horoscope/edit/:id" element={<HoroscopeEditor />} />
              <Route path="/cms/blog/new" element={<ArticleEditor />} />
              <Route path="/cms/blog/edit/:id" element={<ArticleEditor />} />
              <Route path="/cms/orders/:orderNumber" element={<OrderDetail />} />
              <Route path="/cms/*" element={<CMS />} />
              
              <Route path="/404" element={<NotFound />} />

              <Route path="/:slug/strona/:pageNumber" element={<SlugPage />} />
              <Route path="/:slug" element={<SlugPage />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsentBox />
          <FloatingChatButton />
        </SeoProvider>
    </BrowserRouter>
  );
};

export default App;
